import AuthConstants from "../constants/Auth";
import { fire, base } from "../fire";

class AuthService {
  _get = key => {
    if (localStorage && localStorage.getItem(key))
      return JSON.parse(localStorage.getItem(key)) || null;
    if (sessionStorage && sessionStorage.getItem(key))
      return JSON.parse(sessionStorage.getItem(key)) || null;
    return null;
  };
  _set = (key, value, isLocalStorage) => {
    if (isLocalStorage && localStorage)
      return localStorage.setItem(key, JSON.stringify(value));
    if (sessionStorage)
      return sessionStorage.setItem(key, JSON.stringify(value));
    console.warn("Cannot set either local and session browser storage");
    return null;
  };
  _del = (key, isLocalStorage) => {
    if (isLocalStorage && localStorage) return localStorage.removeItem(key);
    if (sessionStorage) return sessionStorage.removeItem(key);
    return null;
  };

  getToken = () => this._get(AuthConstants.OBJ_KEY_JWT_TOKEN);
  getUser = () => this._get(AuthConstants.OBJ_KEY_USER_INFO);
  getUserId = () => (!this.isAuth() ? null : this.getUser().uid);

  setToken = (value, isLocalStorage = true) =>
    this._set(AuthConstants.OBJ_KEY_JWT_TOKEN, value, isLocalStorage);
  setUserData = (value, isLocalStorage = true) =>
    this._set(AuthConstants.OBJ_KEY_USER_INFO, value, isLocalStorage);

  removeToken = () => this._del(AuthConstants.OBJ_KEY_JWT_TOKEN, true);
  removeUserData = () => this._del(AuthConstants.OBJ_KEY_USER_INFO, true);

  isAuth = () => (this.getToken() == null ? false : true);

  /**
   * Method used at App initialization (index.js) to be able to handle any changes in the auth state of the user
   */
  onAuthChange = user => {
    // console.warn("AuthChanged", user)
    if (user == null && this.isAuth()) {
      console.warn(
        "User is logged out in Firebase but still logged in localy. Let's logout him localy now."
      );
      this.logout();
    }
  };

  login = data => {
    const { email, password } = data;
    let user = null;

    let attributes = [
      { name: 'Annual Electricity Consumption', unit: 'KWh/m²', ref: 0 },
      { name: 'Annual Electricity Cost', unit: '€/m².yr', ref: 1 },
      { name: "Annual Primary Energy Consumption", unit: 'KWh/m².yr', ref: 2 },
      { name: "Efficacy", unit: 'lm/W', ref: 3 },
      { name: 'Electric Power Density', unit: 'W/m²', ref: 4 },
      { name: 'Median Daylight Factor', unit: '%', ref: 5 },
      { name: 'Minimum Daylight Factor', unit: '%', ref: 6 },
      { name: "Aperture Ratio", unit: '%', ref: 7 },
      { name: "Glazing Index", unit: '%', ref: 8 },
      { name: "Lighting System Luminous Efficacy", unit: 'lm/W', ref: 9 },
      { name: "System Efficiency", unit: '%', ref: 10 },
      { name: "Minimum Aperture Ratio", unit: '%', ref: 11 },
      { name: "Annual Electricity Cost", unit: '€/y', ref: 12 },
      { name: "Total Electric Power", unit: 'W', ref: 13 },
    ]

    return new Promise((resolve, reject) =>
      // Challenge email and password given with Firebase authentification
      fire
        .auth()
        .signInWithEmailAndPassword(email, password)
        .then(userData => (user = userData.user))
        // Get a JWT Token for later use
        .then(() => user.getIdToken())
        .then(token => this.setToken(token))
        // Retrieve additional information of user
        .then(() => base.fetch(`/users/${user.uid}`, {}))
        // Merge user information retrieve from Firebase auth and Firebase database
        .then(userInfos => {
          if (!userInfos.attributes) {
            userInfos.attributes = attributes
            base.post(`users/${user.uid}`, {
              data: { ...userInfos, attributes: attributes, email: user.email }
            })
            this.setUserData({ uid: user.uid, email: user.email, ...userInfos })
          } else if (!userInfos.email) {
            base.post(`users/${user.uid}`, {
              data: { ...userInfos, email: user.email }
            })
            this.setUserData({ uid: user.uid, email: user.email, ...userInfos })
          } else {
            this.setUserData({ uid: user.uid, email: user.email, ...userInfos })
          }
        }
        )
        .then(() => resolve())
        .catch(e => reject(e))
    );
  };

  logout = () => {
    fire.auth().signOut();
    this.removeToken();
    this.removeUserData();
  };

  createAccountWithEmailAndPassword = data => {
    const {
      email,
      password,
      activity,
      company,
      country,
      firstname,
      lastname
    } = data;

    let attributes = [
      { name: 'Annual Electricity Consumption', unit: 'KWh/m²', ref: 0 },
      { name: 'Annual Electricity Cost', unit: '€/m².yr', ref: 1 },
      { name: "Annual Primary Energy Consumption", unit: 'KWh/m².yr', ref: 2 },
      { name: "Efficacy", unit: 'lm/W', ref: 3 },
      { name: 'Electric Power Density', unit: 'W/m²', ref: 4 },
      { name: 'Median Daylight Factor', unit: '%', ref: 5 },
      { name: 'Minimum Daylight Factor', unit: '%', ref: 6 },
      { name: "Aperture Ratio", unit: '%', ref: 7 },
      { name: "Glazing Index", unit: '%', ref: 8 },
      { name: "Lighting System Luminous Efficacy", unit: 'lm/W', ref: 9 },
      { name: "System Efficiency", unit: '%', ref: 10 },
      { name: "Minimum Aperture Ratio", unit: '%', ref: 11 },
      { name: "Annual Electricity Cost", unit: '€/y', ref: 12 },
      { name: "Total Electric Power", unit: 'W', ref: 13 },
    ]

    return new Promise((resolve, reject) =>
      // Create a user in the Firebase authentification user list
      fire
        .auth()
        .createUserWithEmailAndPassword(email, password)
        .then(data => {
          const { user } = data;
          const { uid } = user;

          // Create a child node in the Firebase database users node to store additional user information
          base
            .post(`users/${uid}`, {
              data: { activity, company, country, firstname, lastname, attributes, email }
            })
            .then(() => user.sendEmailVerification())
            .then(() => resolve())
            .catch(e => reject(e));
        })
        .catch(e => {
          reject(e);
        })
    );
  };

  resetPassword = email => {
    return fire.auth().sendPasswordResetEmail(email);
  };
}

export default new AuthService();
